// frontend/src/LogComparison.jsx — Drop-in replacement
import React, { useMemo, useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import './App.css';
import { Link } from 'react-router-dom';
import FeedbackModal from './FeedbackModal';
import { sendFeedback } from './api';
import {
  auth,
  onAuthStateChanged,
  signInWithGoogle,
  signOutUser,
} from './firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  years, models, engines, injectors, mapSensors, throttles,
  powerAdders, transmissions, tireHeights, gearRatios, fuels,
  fordModels, fordEngines, fordTransmissions
} from './ui/options';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// ── Design tokens (matches MainApp) ───────────────────────
const T = {
  // Surfaces — cool graphite, machined-aluminium cast
  bg:       '#0A0C0D',
  void:     '#0A0C0D',
  panel:    '#14181A',
  card:     '#14181A',
  cardHi:   '#1C2226',
  steel:    '#1C2226',
  border:   '#2A3339',
  borderHi: '#3A464D',
  // Signals — each hue carries meaning
  green:    '#00E676',
  greenDim: '#00A854',
  greenLo:  'rgba(0,230,118,0.08)',
  greenGlow:'rgba(0,230,118,0.15)',
  ice:      '#7DD3FC',
  blue:     '#7DD3FC',
  blueLo:   'rgba(125,211,252,0.08)',
  amber:    '#FFB020',
  amberLo:  'rgba(255,176,32,0.08)',
  red:      '#FF4D4D',
  orange:   '#FF8A3D',
  // Ink
  text:     '#E8EDEF',
  muted:    '#78888F',
  faint:    '#4A585E',
  // Type stacks
  fDisplay: "'Archivo', system-ui, sans-serif",
  fBody:    "'IBM Plex Sans', system-ui, sans-serif",
  fData:    "'IBM Plex Mono', ui-monospace, monospace",
};

const css = {
  page:   { background: T.bg, color: T.text, minHeight: '100vh', fontFamily: T.fBody },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0 28px', height: 64,
    background: 'linear-gradient(135deg, #0a1a0a 0%, #0f280f 50%, #0a1a0a 100%)',
    borderBottom: `1px solid ${T.border}`,
    
    flexWrap: 'wrap', gap: 10,
  },
  logo: {
    fontSize: 18, fontWeight: 700, color: T.green,
    letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 10,
  },
  headerRight: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  shell:  { padding: '24px', maxWidth: 1600, margin: '0 auto' },

  card: {
    background: T.card, border: `1px solid ${T.border}`,
    borderRadius: 10, padding: 18,
  },
  cardHighlight: {
    background: T.card, border: `1px solid ${T.borderHi}`,
    borderRadius: 10, padding: 18,
    
  },

  sectionTitle: {
    margin: '0 0 14px', fontSize: 13, fontWeight: 600,
    letterSpacing: 1.2, textTransform: 'uppercase',
    color: T.green, opacity: 0.9,
  },

  input: {
    width: '100%', background: 'rgba(0,0,0,0.25)',
    border: `1px solid ${T.border}`, borderRadius: 6,
    padding: '9px 12px', color: T.text,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  },
  select: {
    width: '100%', background: 'rgba(0,0,0,0.25)',
    border: `1px solid ${T.border}`, borderRadius: 6,
    padding: '9px 12px', color: T.text, fontSize: 13,
    outline: 'none', appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%2300E676\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat', backgroundPosition: 'calc(100% - 12px) 50%',
    paddingRight: 32, boxSizing: 'border-box',
  },

  btnPrimary: {
    background: T.green, color: '#000', fontWeight: 700,
    border: 'none', borderRadius: 6, padding: '10px 20px',
    cursor: 'pointer', fontSize: 13, letterSpacing: 0.3,
    whiteSpace: 'nowrap',
  },
  btnGhost: {
    background: 'transparent', color: T.muted,
    border: `1px solid ${T.border}`, borderRadius: 6,
    padding: '8px 14px', cursor: 'pointer', fontSize: 13,
    whiteSpace: 'nowrap',
  },
  btnWarn: {
    background: T.amberLo, color: T.amber,
    border: `1px solid rgba(255,176,32,0.3)`, borderRadius: 6,
    padding: '8px 14px', cursor: 'pointer', fontSize: 13,
    whiteSpace: 'nowrap',
  },
  btnNav: {
    background: T.greenLo, color: T.green,
    border: `1px solid ${T.borderHi}`, borderRadius: 6,
    padding: '8px 16px', cursor: 'pointer', fontSize: 13,
    textDecoration: 'none', fontWeight: 600, letterSpacing: 0.3,
    whiteSpace: 'nowrap',
  },
  btnBlue: {
    background: T.blueLo, color: T.blue,
    border: `1px solid rgba(125,211,252,0.25)`, borderRadius: 6,
    padding: '7px 14px', cursor: 'pointer', fontSize: 13,
    whiteSpace: 'nowrap',
  },
  linkish: {
    background: 'transparent', border: 'none',
    color: T.green, cursor: 'pointer',
    textDecoration: 'underline', padding: 0, fontSize: 13,
  },
};

// ── Auth helpers ───────────────────────────────────────────
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
const maskNameString = (raw) => {
  if (!raw) return 'User';
  let s = raw.trim().replace(/@.*$/, '');
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return 'User';
  const first = capitalize(parts[0]);
  const lastInitial = parts[1] ? parts[1][0].toUpperCase() + '.' : '';
  return lastInitial ? `${first} ${lastInitial}` : first;
};
const maskedDisplayName = (u) => {
  if (!u) return '';
  if (u.displayName) return maskNameString(u.displayName);
  if (u.email) return maskNameString(u.email);
  return 'User';
};

// ── CSV parser ─────────────────────────────────────────────
function parseCSV(raw) {
  const rows = raw.split(/\r?\n/).map(r => r.trim());
  if (!rows.length) return null;

  // Header row: any row naming a known time axis (not just one starting "offset")
  const TIME_ALIASES = ['offset','time','time (s)','timestamp','elapsed time','session time'];
  let headerRowIndex = rows.findIndex(r =>
    r.includes(',') && r.split(',').some(c => TIME_ALIASES.includes(c.trim().toLowerCase()))
  );
  if (headerRowIndex === -1) headerRowIndex = rows.findIndex(r => r.toLowerCase().startsWith('offset'));
  if (headerRowIndex === -1) return null;

  const headers = rows[headerRowIndex].split(',').map(h => h.trim());

  // Scan to the first genuinely numeric row instead of assuming a fixed offset
  let dataStart = headerRowIndex + 1;
  while (dataStart < rows.length) {
    const c = rows[dataStart].split(',');
    const numeric = c.filter(x => x.trim() !== '' && Number.isFinite(parseFloat(x))).length;
    if (c.length > 1 && numeric >= Math.max(2, Math.floor(c.length * 0.4))) break;
    dataStart++;
  }
  const dataRows = rows.slice(dataStart);

  // Exact match first, then a loose contains match
  const col = (cands) => {
    for (const c of cands) {
      const i = headers.findIndex(h => h.toLowerCase() === c.toLowerCase());
      if (i !== -1) return i;
    }
    for (const c of cands) {
      const i = headers.findIndex(h => h.toLowerCase().includes(c.toLowerCase()));
      if (i !== -1) return i;
    }
    return -1;
  };
  const speedIndex = col(['Vehicle Speed (SAE)','Vehicle Speed','Speed (SAE)','Vehicle Speed (VSS)','VSS','Speed']);
  const timeIndex  = col(['Offset','Time (s)','Time','Timestamp','Elapsed Time','Session Time']);
  if (speedIndex === -1 || timeIndex === -1) return null;
  const speed = [], time = [];
  for (let row of dataRows) {
    if (!row.includes(',')) continue;
    const cols = row.split(',');
    const s = parseFloat(cols[speedIndex]);
    const t = parseFloat(cols[timeIndex]);
    if (Number.isFinite(s) && Number.isFinite(t)) { speed.push(s); time.push(t); }
  }
  return (speed.length && time.length) ? { speed, time } : null;
}

// ── Run finder ─────────────────────────────────────────────
function findAllRuns(log, startMPH, endMPH) {
  const { time, speed } = log;
  const runs = [];
  let startTime = null, foundStop = false;
  for (let i = 1; i < speed.length; i++) {
    const v = speed[i];
    if (startMPH === 0) {
      if (!foundStop && v < 1.5) foundStop = true;
      if (foundStop && startTime === null && v > 1.5) startTime = time[i];
    } else {
      if (startTime === null && v >= startMPH && v < endMPH) startTime = time[i];
    }
    if (startTime !== null && v >= endMPH) {
      const endTime = time[i];
      const duration = +(endTime - startTime).toFixed(3);
      const seg = { startTime, endTime, duration, data: [] };
      for (let j = 0; j < speed.length; j++) {
        if (time[j] >= startTime && time[j] <= endTime)
          seg.data.push({ x: +(time[j] - startTime).toFixed(3), y: speed[j] });
      }
      runs.push(seg);
      startTime = null;
      if (startMPH === 0) foundStop = false;
    }
    if (startTime !== null && v > endMPH + 10) startTime = null;
  }
  return runs;
}

function getBestRun(log, startMPH, endMPH) {
  const runs = findAllRuns(log, startMPH, endMPH);
  if (!runs.length) return null;
  return runs.reduce((min, r) => (r.duration < min.duration ? r : min));
}

// Horsepower from trap speed and race weight (Hale/Fox):
//   trap mph = 234 * (hp/weight)^(1/3)   =>   hp = weight * (mph/234)^3
const hpFromTrap = (weightLb, mph) => {
  const w = parseFloat(weightLb), m = parseFloat(mph);
  if (!Number.isFinite(w) || !Number.isFinite(m) || w <= 0 || m <= 0) return null;
  return w * Math.pow(m / 234, 3);
};
const parseTrapRange = (raw) => {
  if (!raw) return null;
  const m = String(raw).match(/([\d.]+)\s*[\u2013-]\s*([\d.]+)/);
  if (m) return [parseFloat(m[1]), parseFloat(m[2])];
  const one = String(raw).match(/([\d.]+)/);
  return one ? [parseFloat(one[1]), parseFloat(one[1])] : null;
};

// ── Skeleton ───────────────────────────────────────────────
function Skeleton({ height = 16, width = '100%', style = {} }) {
  return (
    <div style={{
      height, width, borderRadius: 4,
      background: 'linear-gradient(90deg, #151e15 25%, #1e2d1e 50%, #151e15 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
      ...style,
    }} />
  );
}

// ── Badge pill ─────────────────────────────────────────────
function Pill({ children, color = T.green, bg = T.greenLo }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 99,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
      background: bg, color, border: `1px solid ${color}22`,
    }}>
      {children}
    </span>
  );
}

// ── Rank medal ─────────────────────────────────────────────
function RankMedal({ rank }) {
  if (rank === 1) return <span title="1st">🥇</span>;
  if (rank === 2) return <span title="2nd">🥈</span>;
  if (rank === 3) return <span title="3rd">🥉</span>;
  return <span style={{ color: T.muted, fontSize: 12 }}>#{rank}</span>;
}

// ═══════════════════════════════════════════════════════════
export default function LogComparison() {
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 1200);
  useEffect(() => {
    const fn = () => setIsNarrow(window.innerWidth < 1200);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // Auth
  const [user, setUser]               = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail]             = useState('');
  const [pw, setPw]                   = useState('');
  const [authBusy, setAuthBusy]       = useState(false);
  const [status, setStatus]           = useState('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u || null));
    return () => unsub();
  }, []);

  // Feedback
  const [showFeedback, setShowFeedback] = useState(false);

  // Logs
  const [log1, setLog1]         = useState(null);
  const [log2, setLog2]         = useState(null);
  const [log1File, setLog1File] = useState(null);
  const [log2File, setLog2File] = useState(null);
  const [log1Name, setLog1Name] = useState('');
  const [log2Name, setLog2Name] = useState('');

  // Graph
  const [graphData, setGraphData]       = useState(null);
  const [leaderOverlay, setLeaderOverlay] = useState(null);
  const [summary, setSummary]           = useState({});
  const [interval, setInterval]         = useState('60-130');

  // Vehicle
  const [platform, setPlatform] = useState('');
  const [vehicle, setVehicle] = useState({
    name: '', year: '', model: '', engine: '', injectors: '', map: '',
    throttle: '', power: '', trans: '', tire: '', gear: '', fuel: ''
  });

  // AI Review
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewText, setReviewText]       = useState('');
  const [reviewError, setReviewError]     = useState('');
  const [raceWeight, setRaceWeight]       = useState('');

  // Leaderboard
  const [leaderboard, setLeaderboard]   = useState({ results: [], total: 0 });
  const [loadingLB, setLoadingLB]       = useState(false);
  const [filters, setFilters]           = useState({ year:'', model:'', engine:'', power:'', fuel:'', trans:'' });
  const [sort, setSort]                 = useState('time_seconds:asc');
  const [submitting, setSubmitting]     = useState(false);

  const ranges = useMemo(() => ({
    '0-60': [0, 60], '40-100': [40, 100], '60-130': [60, 130]
  }), []);

  // ── AI Review ─────────────────────────────────────────
  const runLogReview = async (file) => {
    if (!file) return;
    setReviewLoading(true);
    setReviewText('');
    setReviewError('');
    try {
      const form = new FormData();
      form.append('log', file);
      const res = await fetch(`${API_BASE}/ai-review`, { method: 'POST', body: form });
      if (!res.ok) throw new Error(`Review failed (${res.status})`);
      const text = await res.text();
      // Strip the ===DYNO=== JSON payload the backend appends (not needed here)
      const stripped = text.split('===DYNO===')[0];
      const [quickChecks, aiPart] = stripped.split('===SPLIT===');
      const combined = (quickChecks||'').trim() + (aiPart ? `\n\nAI Review:\n${aiPart.trim()}` : '');
      setReviewText(combined || 'No output returned.');
    } catch (err) {
      setReviewError(err?.message || 'Log review failed.');
    } finally {
      setReviewLoading(false);
    }
  };

  // ── File handling ──────────────────────────────────────
  const handleFileChange = (e, setParsed, setFileRef, setName, isLog1) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileRef(file);
    setName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCSV(reader.result);
      setParsed(parsed);
      setStatus(parsed ? '' : '❌ Failed to parse CSV — check format.');
      if (isLog1 && file) runLogReview(file);
    };
    reader.readAsText(file);
  };

  // ── Graph generation ───────────────────────────────────
  const computeBestForInterval = (logObj) => {
    if (!logObj) return null;
    const [start, end] = ranges[interval];
    const best = getBestRun(logObj, start, end);
    return best ? best.duration : null;
  };

  const handleGenerateGraph = (forcedOverlay = undefined) => {
    if (!log1 && !log2 && !leaderOverlay && !forcedOverlay) {
      setStatus('Upload at least one log file first.'); return;
    }
    const [startMPH, endMPH] = ranges[interval];
    const r1 = log1 ? getBestRun(log1, startMPH, endMPH) : null;
    const r2 = log2 ? getBestRun(log2, startMPH, endMPH) : null;
    if (!r1 && !r2 && !leaderOverlay && !forcedOverlay) {
      setStatus(`No valid ${interval} run found.`); return;
    }

    const datasets = [];
    let xEnd = 0;

    if (r1) {
      datasets.push({ label: log1Name || 'Log 1', data: r1.data, tension: 0.1, parsing: false, borderColor: T.green, backgroundColor: 'rgba(0,230,118,0.1)', borderWidth: 2, pointRadius: 0 });
      datasets.push({ label: `${startMPH}–${endMPH}: ${r1.duration}s`, data: [{ x: r1.data.at(-1).x, y: r1.data.at(-1).y }], pointRadius: 7, pointStyle: 'triangle', showLine: false, borderColor: T.green });
      xEnd = Math.max(xEnd, r1.duration);
    }
    if (r2) {
      datasets.push({ label: log2Name || 'Log 2', data: r2.data, tension: 0.1, parsing: false, borderColor: T.blue, backgroundColor: 'rgba(125,211,252,0.1)', borderWidth: 2, pointRadius: 0 });
      datasets.push({ label: `${startMPH}–${endMPH} (L2): ${r2.duration}s`, data: [{ x: r2.data.at(-1).x, y: r2.data.at(-1).y }], pointRadius: 7, pointStyle: 'triangle', showLine: false, borderColor: T.blue });
      xEnd = Math.max(xEnd, r2.duration);
    }
    const overlayToUse = forcedOverlay !== undefined ? forcedOverlay : leaderOverlay;
    if (overlayToUse) {
      datasets.push({ label: overlayToUse.label, data: overlayToUse.data || [], borderDash: [6, 4], tension: 0.1, parsing: false, borderColor: T.orange, backgroundColor: 'rgba(255,159,64,0.1)', borderWidth: 2, pointRadius: 0 });
      const lastX = overlayToUse.data?.length ? overlayToUse.data.at(-1).x : 0;
      xEnd = Math.max(xEnd, lastX);
    }

    setGraphData({
      datasets,
      options: {
        responsive: true, maintainAspectRatio: false, parsing: false,
        plugins: {
          tooltip: { callbacks: { label: ctx => `Speed: ${ctx.parsed.y} mph @ ${ctx.parsed.x}s` } },
          legend: { labels: { color: T.muted, font: { size: 12 } } }
        },
        scales: {
          x: { type: 'linear', min: 0, max: xEnd + 0.5, title: { display: true, text: 'Time (s)', color: T.muted }, ticks: { color: T.muted }, grid: { color: '#1a221a' } },
          y: { min: startMPH - 5, max: endMPH + 5, title: { display: true, text: 'Speed (mph)', color: T.muted }, ticks: { color: T.muted }, grid: { color: '#1a221a' } }
        }
      }
    });
    setSummary({ 'Log 1': r1?.duration || null, 'Log 2': r2?.duration || null });
    setStatus('');
  };

  // ── Leaderboard ────────────────────────────────────────
  const fetchLeaderboard = async () => {
    try {
      setLoadingLB(true);
      const params = new URLSearchParams({ interval, limit: '50' });
      if (filters.year)   params.set('year',   filters.year);
      if (filters.model)  params.set('model',  filters.model);
      if (filters.engine) params.set('engine', filters.engine);
      if (filters.power)  params.set('power',  filters.power);
      if (filters.fuel)   params.set('fuel',   filters.fuel);
      if (filters.trans)  params.set('trans',  filters.trans);
      const [sortCol, sortDir] = (sort || 'time_seconds:asc').split(':');
      params.set('sort', sortCol);
      params.set('dir',  sortDir);
      const res = await fetch(`${API_BASE}/api/leaderboard?${params.toString()}`);
      const json = await res.json();
      setLeaderboard({ results: json.results || [], total: json.total ?? (json.results?.length || 0) });
    } catch { setLeaderboard({ results: [], total: 0 }); }
    finally { setLoadingLB(false); }
  };

  useEffect(() => { fetchLeaderboard(); /* eslint-disable-next-line */ }, [interval, sort]);
  useEffect(() => { fetchLeaderboard(); /* eslint-disable-next-line */ }, [filters.year, filters.model, filters.engine, filters.power, filters.fuel, filters.trans]);

  const overlayLeader = async (runId, label) => {
    try {
      const res = await fetch(`${API_BASE}/api/run/${runId}?interval=${encodeURIComponent(interval)}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to fetch run');
      const trace = json.trace || [];
      if (!trace.length) { setStatus('Leader has no trace for this interval.'); return; }
      const overlayObj = { label: `${label} (Leader)`, data: trace };
      setLeaderOverlay(overlayObj);
      handleGenerateGraph(overlayObj);
    } catch (e) { setStatus(`Overlay error: ${String(e.message || e)}`); }
  };

  const clearOverlay = () => { setLeaderOverlay(null); handleGenerateGraph(null); };

  const submitToLeaderboard = async () => {
    setStatus('');
    if (!user) { setStatus('⚠ Please sign in to submit your run.'); return; }
    const file = log1File || log2File;
    const parsed = log1 || log2;
    if (!file || !parsed) { setStatus('Upload a CSV first.'); return; }
    const best = computeBestForInterval(parsed);
    if (best == null) { setStatus(`No valid ${interval} run found.`); return; }
    const alias = maskedDisplayName(user);
    const vehicleInfo = { ...vehicle, make: platform==='ford' ? 'Ford' : 'Dodge', name: alias, interval, timeSeconds: best };
    const fd = new FormData();
    fd.append('log', file, file.name || 'log.csv');
    fd.append('vehicleInfo', JSON.stringify(vehicleInfo));
    fd.append('consent', 'true');
    try {
      setSubmitting(true);
      setStatus('Submitting…');
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_BASE}/api/submit-run`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
        body: fd
      });
      if (!res.ok) { const t = await res.text(); throw new Error(t || 'Submission failed.'); }
      const json = await res.json();
      setStatus(`✅ Submitted! ${interval} = ${best.toFixed(2)}s`);
      await fetchLeaderboard();
    } catch (err) { setStatus(`❌ ${String(err.message || err)}`); }
    finally { setSubmitting(false); }
  };

  // ── Auth handlers ──────────────────────────────────────
  const handleGoogleLogin = async () => {
    try { await signInWithGoogle(); setStatus(''); }
    catch (e) { if (e?.code !== 'auth/popup-closed-by-user') setStatus(`Sign-in error: ${e?.message || e}`); }
  };

  const handleEmailSignIn = async () => {
    if (!email || !pw) { setStatus('Enter email and password.'); return; }
    try { setAuthBusy(true); await signInWithEmailAndPassword(auth, email.trim(), pw); setStatus(''); setPw(''); setShowEmailForm(false); }
    catch (e) { setStatus(e?.message || 'Sign-in failed.'); }
    finally { setAuthBusy(false); }
  };

  const handleEmailSignUp = async () => {
    if (!email || !pw) { setStatus('Enter email and password.'); return; }
    try { setAuthBusy(true); await createUserWithEmailAndPassword(auth, email.trim(), pw); setStatus('Account created.'); setPw(''); setShowEmailForm(false); }
    catch (e) { setStatus(e?.message || 'Sign-up failed.'); }
    finally { setAuthBusy(false); }
  };

  const handleForgotPassword = async () => {
    if (!email) { setStatus('Enter your email first.'); return; }
    try { setAuthBusy(true); await sendPasswordResetEmail(auth, email.trim()); setStatus('Reset email sent.'); }
    catch (e) { setStatus(e?.message || 'Could not send reset email.'); }
    finally { setAuthBusy(false); }
  };

  const handleSubmitFeedback = async ({ email: fbEmail, page, message }) => {
    try {
      await sendFeedback({ message, meta: { page: page || 'LogComparison', interval, user: user ? maskedDisplayName(user) : 'Guest', email: fbEmail || null, status } });
      setStatus('Feedback sent — thanks!');
      return true;
    } catch (e) { setStatus(`Feedback error: ${e?.message || e}`); return false; }
  };

  // ── Review text parser (reuse from MainApp style) ──────
  const reviewLines = useMemo(() => {
    if (!reviewText) return [];
    const checklistOnly = reviewText.split('\n\nAI Review:')[0].trim();
    return checklistOnly.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
      let type = 'info', body = line;
      if (line.startsWith('CRITICAL:'))      { type = 'critical'; body = line.slice(9).trim(); }
      else if (line.startsWith('WARN:'))     { type = 'warn';     body = line.slice(5).trim(); }
      else if (line.startsWith('OK:'))       { type = 'ok';       body = line.slice(3).trim(); }
      else if (line.startsWith('STAT:'))     { type = 'stat';     body = line.slice(5).trim(); }
      else if (line.startsWith('INFO:'))     { type = 'info';     body = line.slice(5).trim(); }
      else if (line.startsWith('⚠️') || line.startsWith('🚨')) { type = 'warn'; }
      else if (line.startsWith('✅')) { type = 'ok'; }
      else if (['📈','🚀','🚦','📊','🌀','🎯'].some(e => line.startsWith(e))) { type = 'stat'; }
      return { type, body };
    });
  }, [reviewText]);

  // Extract AI narrative (Summary + What This Means For You) from reviewText
  const reviewAiParts = useMemo(() => {
    if (!reviewText) return { summary: null, action: null };
    // Split on the "AI Review:" marker inserted by runLogReview
    const parts = reviewText.split('AI Review:');
    if (parts.length < 2) return { summary: null, action: null };
    const aiSection = parts[1].trim();
    // Strip leading "Summary" heading if the AI included it
    const withoutSummaryHead = aiSection.replace(/^Summary\s*/i, '').trim();
    // Split into summary + action on "What This Means For You"
    const splitIdx = withoutSummaryHead.search(/What This Means For You/i);
    if (splitIdx === -1) return { summary: withoutSummaryHead || null, action: null };
    const summary = withoutSummaryHead.slice(0, splitIdx).trim();
    const action  = withoutSummaryHead.slice(splitIdx).replace(/^What This Means For You\s*/i, '').trim();
    return { summary: summary || null, action: action || null };
  }, [reviewText]);

  // ── Layout breakpoints ─────────────────────────────────
  // 3-col on wide, 1-col on narrow
  const layoutStyle = isNarrow
    ? { display: 'grid', gridTemplateColumns: '1fr', gap: 16 }
    : { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 };

  return (
    <div style={css.page} className="st-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800&family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        select option { background: #111811; }
        input[type=file] { display: none; }
        .upload-label {
          display: flex; align-items: center; gap: 8px;
          padding: 9px 14px; border-radius: 7px; width: 100%;
          border: 2px dashed ${T.border}; cursor: pointer;
          color: ${T.muted}; font-size: 13px;
          transition: border-color 0.2s, color 0.2s; box-sizing: border-box;
        }
        .upload-label:hover { border-color: ${T.green}; color: ${T.green}; }
        .upload-label.has-file { border-color: ${T.borderHi}; color: ${T.text}; border-style: solid; }
        .lb-row:hover { background: rgba(0,230,118,0.04); }
        .lb-row td { padding: 9px 10px; border-bottom: 1px solid ${T.border}; font-size: 13px; }
        .lb-row-top td { color: #eaff9c; }
        .lb-row-top { background: rgba(0,230,118,0.03); }
      `}</style>

      {/* ── HEADER ─────────────────────────────────────── */}
      <header className="st-header">
        <div className="st-logo">
          <div className="st-logo-icon">
            <svg width="18" height="18" viewBox="0 0 22 22" fill="none">
              <circle cx="11" cy="11" r="10" stroke={T.green} strokeWidth="1.5"/>
              <path d="M7 11 L10 14 L15 8" stroke={T.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          Satera Tuning
          <span className="st-logo-sub">Log Comparison</span>
          <span className="st-beta">Beta</span>
        </div>

        <div style={css.headerRight}>
          <Link to="/ai-review" className="st-btn-nav">AI Log Review →</Link>
          <button className='st-btn-ghost' onClick={() => setShowFeedback(true)}>Report a Bug</button>

          {!user ? (
            <>
              <button style={css.btnBlue} onClick={handleGoogleLogin}>Sign in with Google</button>
              <button style={css.btnGhost} onClick={() => { setShowEmailForm(v => !v); setStatus(''); }}>
                {showEmailForm ? 'Hide login' : 'Email login'}
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 12, background: '#0c180c', border: `1px solid ${T.border}`, color: T.green, padding: '5px 10px', borderRadius: 99 }}>
                {maskedDisplayName(user)}
              </span>
              <button style={css.btnGhost} onClick={signOutUser}>Sign out</button>
            </>
          )}
        </div>
      </header>

      {/* ── EMAIL AUTH FORM ─────────────────────────────── */}
      {!user && showEmailForm && (
        <div style={{ background: T.card, borderBottom: `1px solid ${T.border}`, padding: '14px 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'end', maxWidth: 700 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: T.muted, marginBottom: 4 }}>Email</label>
              <input type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} style={css.input} autoComplete="email"/>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: T.muted, marginBottom: 4 }}>Password</label>
              <input type="password" placeholder="••••••••" value={pw} onChange={e => setPw(e.target.value)} style={css.input} autoComplete="current-password"/>
            </div>
            <button disabled={authBusy} onClick={handleEmailSignIn} style={css.btnPrimary}>
              {authBusy ? 'Working…' : 'Sign in'}
            </button>
            <button disabled={authBusy} onClick={handleEmailSignUp} style={{ ...css.btnGhost, color: T.amber, borderColor: 'rgba(255,176,32,0.3)' }}>
              {authBusy ? 'Working…' : 'Create account'}
            </button>
          </div>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16 }}>
            <button disabled={authBusy} onClick={handleForgotPassword} style={css.linkish}>Forgot password?</button>
            {status && <span style={{ fontSize: 13, color: status.startsWith('❌') ? T.red : T.muted }}>{status}</span>}
          </div>
        </div>
      )}

      <div style={css.shell}>

        {/* ── HOW IT WORKS ── */}
        <div className="st-how-it-works" style={{ marginBottom: 16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
            <span style={{ fontSize:14 }}>🏁</span>
            <span style={{ fontFamily:T.fDisplay, fontSize:13, fontWeight:600, letterSpacing:2, textTransform:'uppercase', color:T.green }}>How It Works</span>
          </div>
          <div className="st-how-it-works-steps">
            <div className="st-step">
              <div className="st-step-num">1</div>
              <div className="st-step-text"><strong>Upload Log 1</strong>HP Tuners CSV — triggers AI review automatically</div>
            </div>
            <span className="st-step-arrow">›</span>
            <div className="st-step">
              <div className="st-step-num">2</div>
              <div className="st-step-text"><strong>Pick Interval</strong>Choose 0-60, 40-100, or 60-130 mph</div>
            </div>
            <span className="st-step-arrow">›</span>
            <div className="st-step">
              <div className="st-step-num">3</div>
              <div className="st-step-text"><strong>Generate Graph</strong>Compare logs or overlay a leaderboard run</div>
            </div>
            <span className="st-step-arrow">›</span>
            <div className="st-step">
              <div className="st-step-num">4</div>
              <div className="st-step-text"><strong>Submit Your Time</strong>Sign in and add your run to the leaderboard</div>
            </div>
          </div>
          <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid rgba(0,230,118,0.08)', display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:11, color:T.muted }}>Not sure how to export a CSV from HP Tuners?</span>
            <a
              href="https://youtu.be/PJ2-haxPl6I"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display:'inline-flex', alignItems:'center', gap:6,
                padding:'5px 12px', borderRadius:6,
                background:'rgba(255,0,0,0.08)', border:'1px solid rgba(255,80,80,0.25)',
                color:'#ff6b6b', fontSize:11, fontWeight:600,
                letterSpacing:0.5, textDecoration:'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.background='rgba(255,0,0,0.15)'; e.currentTarget.style.borderColor='rgba(255,80,80,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='rgba(255,0,0,0.08)'; e.currentTarget.style.borderColor='rgba(255,80,80,0.25)'; }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="#ff6b6b">
                <path d="M23 7s-.3-2-1.2-2.8c-1.1-1.2-2.4-1.2-3-1.3C16.1 2.8 12 2.8 12 2.8s-4.1 0-6.8.2c-.6.1-1.9.1-3 1.3C1.3 5 1 7 1 7S.8 9.2.8 11.5v2.1c0 2.3.2 4.5.2 4.5s.3 2 1.2 2.8c1.1 1.2 2.6 1.1 3.3 1.2C7.6 22.2 12 22.2 12 22.2s4.1 0 6.8-.3c.6-.1 1.9-.1 3-1.3.9-.8 1.2-2.8 1.2-2.8s.2-2.2.2-4.5v-2.1C23.2 9.2 23 7 23 7zM9.7 15.5V8.4l8.1 3.6-8.1 3.5z"/>
              </svg>
              Watch: How to Export CSV from HP Tuners
            </a>
          </div>
        </div>

        {/* ── PLATFORM SELECTOR ── */}
        <div style={{ ...css.card, marginBottom: 20 }}>
          <p style={{ fontSize:12, color:T.green, letterSpacing:2, textTransform:'uppercase', fontWeight:700, margin:'0 0 4px' }}>
            Step 1 — Select Your Vehicle Platform
          </p>
          <p style={{ fontSize:12, color:T.muted, margin:'0 0 14px' }}>
            Choose your platform to unlock log upload and vehicle options.
          </p>
          <div className="st-platform">
            {[
                  ['mopar','🔧','Mopar / Gen3 HEMI','Charger · Challenger · Durango · Ram'],
              ['ford', '🐎','Ford Coyote',      'Mustang GT · GT500 · F-150 · Dark Horse'],
            ].map(([p, icon, label, sub]) => (
              <button key={p} onClick={() => { setPlatform(p); setVehicle(v => ({...v, model:'', engine:'', trans:''})); }}
                style={{
                  flex:1, padding:'18px 14px', borderRadius:10, cursor:'pointer', textAlign:'center',
                  border: platform===p ? '2px solid #00E676' : '1.5px solid #2A3339',
                  background: platform===p ? 'rgba(0,230,118,0.07)' : 'rgba(0,0,0,0.25)',
                  transition:'all 0.2s',
                  boxShadow: platform===p ? '0 0 18px rgba(0,230,118,0.18)' : 'none',
                }}>
                <div style={{ fontSize:32, marginBottom:8 }}>{icon}</div>
                <div style={{ fontSize:15, fontWeight:700, color: platform===p ? T.green : T.text, marginBottom:5 }}>{label}</div>
                <div style={{ fontSize:11, color:T.faint, lineHeight:1.4 }}>{sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── UPLOAD CONTROLS — gated behind platform ─── */}
        {!platform && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:140, color:T.faint, fontSize:13, textAlign:'center', flexDirection:'column', gap:8, border:'1.5px dashed #2A3339', borderRadius:10, padding:24, marginBottom:20 }}>
            <div style={{ fontSize:28 }}>👆</div>
            <div>Select your vehicle platform above to upload logs</div>
          </div>
        )}
        {platform && <div style={{ ...css.cardHighlight, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            <p className='st-section-title' style={{ margin:0 }}>Upload & Compare</p>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: T.muted }}>Interval:</span>
              {['0-60', '40-100', '60-130'].map(iv => (
                <button key={iv} onClick={() => setInterval(iv)}
                  style={{
                    ...css.btnGhost,
                    padding: '5px 12px', fontSize: 12,
                    ...(interval === iv ? { background: T.greenLo, color: T.green, borderColor: T.green } : {}),
                  }}>
                  {iv} mph
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 14 }}>
            {/* Log 1 */}
            <div>
              <label style={{ display: 'block', fontSize: 11, color: T.green, marginBottom: 6, fontWeight: 600, letterSpacing: 0.8 }}>
                LOG 1 — Required
              </label>
              <label htmlFor="log1Input" className={`upload-label${log1 ? ' has-file' : ''}`}>
                <span>📂</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log1Name || 'Choose HP Tuners CSV…'}
                </span>
                {log1 && <span style={{ marginLeft: 'auto', color: T.green, flexShrink: 0 }}>✓</span>}
              </label>
              <input id="log1Input" type="file" accept=".csv"
                onChange={e => handleFileChange(e, setLog1, setLog1File, setLog1Name, true)}/>
              <p style={{ fontSize: 11, color: T.faint, marginTop: 4 }}>Auto-triggers AI review on upload</p>
            </div>

            {/* Log 2 */}
            <div>
              <label style={{ display: 'block', fontSize: 11, color: T.muted, marginBottom: 6, fontWeight: 600, letterSpacing: 0.8 }}>
                LOG 2 — Optional
              </label>
              <label htmlFor="log2Input" className={`upload-label${log2 ? ' has-file' : ''}`}>
                <span>📂</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log2Name || 'Choose comparison CSV…'}
                </span>
                {log2 && <span style={{ marginLeft: 'auto', color: T.blue, flexShrink: 0 }}>✓</span>}
              </label>
              <input id="log2Input" type="file" accept=".csv"
                onChange={e => handleFileChange(e, setLog2, setLog2File, setLog2Name, false)}/>
              <p style={{ fontSize: 11, color: T.faint, marginTop: 4 }}>Compare against Log 1 on graph</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => handleGenerateGraph()} style={css.btnPrimary} disabled={!log1 && !log2}>
              Generate Graph
            </button>
            {leaderOverlay && (
              <button onClick={clearOverlay} style={css.btnWarn}>✕ Clear Overlay</button>
            )}
            {status && (
              <span style={{ fontSize: 13, color: status.startsWith('❌') || status.startsWith('⚠') ? T.amber : T.muted }}>
                {status}
              </span>
            )}
          </div>
        </div>}

        {/* ── VEHICLE BAR (horizontal, full width) ────── */}
        {platform && <div style={{ ...css.card, marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <p className='st-section-title' style={{ margin:0 }}>Vehicle Details</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={submitToLeaderboard} disabled={submitting} style={{ ...css.btnPrimary, padding: '8px 18px' }}>
                {submitting ? 'Submitting…' : '🏆 Submit to Leaderboard'}
              </button>
              {!user && <span style={{ fontSize: 11, color: T.amber }}>Sign in required</span>}
              {status && !showEmailForm && (
                <span style={{ fontSize: 12, color: status.startsWith('✅') ? T.green : status.startsWith('❌') ? T.red : T.muted }}>
                  {status}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            <input
              name="name"
              placeholder={user ? maskedDisplayName(user) : 'Display name'}
              value={user ? maskedDisplayName(user) : vehicle.name}
              onChange={user ? undefined : e => setVehicle(v => ({ ...v, name: e.target.value }))}
              readOnly={!!user}
              style={{ ...css.input, opacity: user ? 0.6 : 1, cursor: user ? 'not-allowed' : 'text' }}
            />
            <select name="year" value={vehicle.year} onChange={e => setVehicle(v => ({ ...v, year: e.target.value }))} style={css.select}>
              <option value="">Year</option>{years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select name="model" value={vehicle.model} onChange={e => setVehicle(v => ({ ...v, model: e.target.value }))} style={css.select}>
              <option value="">Model</option>{(platform==='ford' ? fordModels : models).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select name="engine" value={vehicle.engine} onChange={e => setVehicle(v => ({ ...v, engine: e.target.value }))} style={css.select}>
              <option value="">Engine</option>{(platform==='ford' ? fordEngines : engines).map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <select name="trans" value={vehicle.trans} onChange={e => setVehicle(v => ({ ...v, trans: e.target.value }))} style={css.select}>
              <option value="">Transmission</option>{(platform==='ford' ? fordTransmissions : transmissions).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select name="power" value={vehicle.power} onChange={e => setVehicle(v => ({ ...v, power: e.target.value }))} style={css.select}>
              <option value="">Power</option>{powerAdders.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select name="fuel" value={vehicle.fuel} onChange={e => setVehicle(v => ({ ...v, fuel: e.target.value }))} style={css.select}>
              <option value="">Fuel</option>{fuels.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select name="gear" value={vehicle.gear} onChange={e => setVehicle(v => ({ ...v, gear: e.target.value }))} style={css.select}>
              <option value="">Rear Gear</option>{gearRatios.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <select name="tire" value={vehicle.tire} onChange={e => setVehicle(v => ({ ...v, tire: e.target.value }))} style={css.select}>
              <option value="">Tire Height</option>{tireHeights.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>}

        {/* ── 2-COLUMN LAYOUT: center + leaderboard ────── */}
        <div style={layoutStyle}>

          {/* ── COL 1: Graph + AI Review ──────────────── */}
          <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
            {/* Speed graph */}
            <div style={css.card}>
              <p className='st-section-title'>Speed vs Time — {interval} mph</p>
              {graphData ? (
                <>
                  <div style={{ height: 280 }}>
                    <Line data={graphData} options={graphData.options}/>
                  </div>
                  <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {summary['Log 1'] && (
                      <span style={{ fontSize: 12, padding: '4px 12px', background: 'rgba(0,230,118,0.08)', borderRadius: 99, color: T.green }}>
                        🚀 {log1Name || 'Log 1'}: {summary['Log 1']}s
                      </span>
                    )}
                    {summary['Log 2'] && (
                      <span style={{ fontSize: 12, padding: '4px 12px', background: 'rgba(125,211,252,0.08)', borderRadius: 99, color: T.blue }}>
                        🚀 {log2Name || 'Log 2'}: {summary['Log 2']}s
                      </span>
                    )}
                    {summary['Log 1'] && summary['Log 2'] && (
                      <span style={{ fontSize: 12, padding: '4px 12px', background: T.amberLo, borderRadius: 99, color: T.amber }}>
                        Δ {Math.abs(summary['Log 1'] - summary['Log 2']).toFixed(3)}s difference
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.faint, fontSize: 13, flexDirection: 'column', gap: 8 }}>
                  <span style={{ fontSize: 28 }}>📈</span>
                  Upload a log and click Generate Graph
                </div>
              )}
            </div>

            {/* AI Review panel */}
            <div style={css.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <p className='st-section-title' style={{ margin:0 }}>AI Log Review — Log 1</p>
                <button
                  onClick={() => log1File && runLogReview(log1File)}
                  disabled={!log1File || reviewLoading}
                  style={{ ...css.btnGhost, fontSize: 12, opacity: (!log1File || reviewLoading) ? 0.5 : 1 }}>
                  {reviewLoading ? '⏳ Reviewing…' : '↻ Re-run'}
                </button>
              </div>

              {!log1File && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: T.faint, fontSize: 13 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🤖</div>
                  Upload Log 1 to generate an AI review
                </div>
              )}

              {reviewLoading && (
                <div style={{ display: 'grid', gap: 8 }}>
                  {[85, 65, 75, 55, 80].map((w, i) => (
                    <Skeleton key={i} height={14} width={`${w}%`} style={{ animationDelay: `${i * 0.12}s` }}/>
                  ))}
                </div>
              )}

              {reviewError && !reviewLoading && (
                <div style={{ padding: '10px 14px', background: 'rgba(255,77,77,0.08)', borderRadius: 6, border: `1px solid rgba(255,77,77,0.2)`, fontSize: 13, color: T.red }}>
                  {reviewError}
                </div>
              )}

              {!reviewLoading && reviewLines.length > 0 && (() => {
                const CHECK_STYLE = {
                  critical: { icon: '🚨', label: 'Critical', color: T.red, bg: 'rgba(255,77,77,0.06)',   border: T.red },
                  warn:     { icon: '⚠️',  label: 'Warning',  color: T.amber, bg: 'rgba(255,176,32,0.06)', border: T.amber },
                  ok:       { icon: '✅',  label: 'Good',     color: T.green, bg: 'rgba(0,230,118,0.05)', border: T.green },
                  stat:     { icon: '📊',  label: 'Data',     color: T.ice, bg: 'rgba(125,211,252,0.05)', border: T.ice },
                  info:     { icon: 'ℹ️',  label: 'Info',     color: T.muted, bg: 'transparent',           border: T.faint },
                };
                const criticals = reviewLines.filter(l => l.type === 'critical');
                const warns     = reviewLines.filter(l => l.type === 'warn');
                const oks       = reviewLines.filter(l => l.type === 'ok');
                const stats     = reviewLines.filter(l => l.type === 'stat');
                // Keep the 1/4 mile caveat next to the figures it explains
                const isQtrNote = (l) => l.type === 'info' && /1\/4 mile/i.test(l.body);
                const qtrNote   = reviewLines.find(isQtrNote);
                const infos     = reviewLines.filter(l => l.type === 'info' && !isQtrNote(l));
                const Row = ({ type, body }) => {
                  const s = CHECK_STYLE[type] || CHECK_STYLE.info;
                  const dashIdx = body.indexOf(' — ');
                  const colonIdx = body.indexOf('. ');
                  let headline = null, detail = body;
                  if (dashIdx !== -1 && dashIdx < 80) {
                    headline = body.slice(0, dashIdx).trim();
                    detail   = body.slice(dashIdx + 3).trim();
                  } else if (colonIdx !== -1 && colonIdx < 80 && (type === 'critical' || type === 'warn')) {
                    headline = body.slice(0, colonIdx + 1).trim();
                    detail   = body.slice(colonIdx + 2).trim();
                  }
                  return (
                    <div style={{ display:'flex', gap:8, padding:'9px 12px', borderRadius:6, background:s.bg, marginBottom:3, borderLeft:`3px solid ${s.border}` }}>
                      <span style={{ fontSize:13, flexShrink:0 }}>{s.icon}</span>
                      <div style={{ flex:1 }}>
                        <span style={{ fontSize:10, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:s.color, display:'block', marginBottom:4 }}>{s.label}</span>
                        {headline && (
                          <span style={{ fontSize:13, fontWeight:700, color:s.color, display:'block', marginBottom:4, lineHeight:1.4 }}>{headline}</span>
                        )}
                        <span style={{ fontSize:12, lineHeight:1.6, color:T.text, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{detail}</span>
                      </div>
                    </div>
                  );
                };
                return (
                  <div style={{ display:'grid', gap:10, marginTop:4 }}>
                    {criticals.length > 0 && (
                      <div style={{ background:'rgba(255,77,77,0.06)', border:'1px solid rgba(255,77,77,0.2)', borderRadius:8, padding:12 }}>
                        <div style={{ fontFamily:T.fDisplay, fontSize:12, fontWeight:700, color:T.red, letterSpacing:2, textTransform:'uppercase', marginBottom:10 }}>⚠ Needs Immediate Attention</div>
                        {criticals.map((l,i) => <Row key={i} {...l}/>)}
                      </div>
                    )}
                    {warns.length > 0 && (
                      <div style={{ background:'rgba(255,176,32,0.04)', border:'1px solid rgba(255,176,32,0.15)', borderRadius:8, padding:12 }}>
                        <div style={{ fontFamily:T.fDisplay, fontSize:12, fontWeight:700, color:T.amber, letterSpacing:2, textTransform:'uppercase', marginBottom:10 }}>Attention Needed</div>
                        {warns.map((l,i) => <Row key={i} {...l}/>)}
                      </div>
                    )}
                    {stats.length > 0 && (
                      <div style={{ background:T.card, border:'1px solid #2A3339', borderRadius:8, padding:12 }}>
                        <div style={{ fontFamily:T.fDisplay, fontSize:12, fontWeight:700, color:T.green, letterSpacing:2, textTransform:'uppercase', marginBottom:10 }}>Performance Data</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                          {(() => {
                            const baseline = platform === 'ford' ? 4100 : 4500;
                            const w = parseFloat(raceWeight);
                            if (!Number.isFinite(w) || w <= 0 || w === baseline) return stats;
                            const dEt = ((w - baseline) / 100) * 0.1;
                            const kHp = w / baseline;
                            return stats.map(l => {
                              const [lab, ...rest] = l.body.split(':');
                              const val = rest.join(':').trim();
                              const shift = (v, fn) => {
                                const m = val.match(/([\d.]+)\s*[\u2013-]\s*([\d.]+)/);
                                if (m) return `${fn(parseFloat(m[1]))}\u2013${fn(parseFloat(m[2]))}`;
                                const o = val.match(/([\d.]+)/);
                                return o ? `${fn(parseFloat(o[1]))}` : val;
                              };
                              if (/1\/4 mile ET/i.test(lab))
                                return { ...l, body: `${lab}: ${shift(val, n => (n + dEt).toFixed(1))}s` };
                              if (/Estimated Power/i.test(lab))
                                return { ...l, body: `${lab}: ${shift(val, n => Math.round(n * kHp / 5) * 5)} hp` };
                              if (/Power to Weight/i.test(lab)) {
                                const hpStat = stats.find(x => /Estimated Power/i.test(x.body));
                                const hm = hpStat && hpStat.body.match(/([\d.]+)\s*[\u2013-]\s*([\d.]+)/);
                                const mid = hm ? ((parseFloat(hm[1]) + parseFloat(hm[2])) / 2) * kHp : null;
                                return mid ? { ...l, body: `${lab}: ${(w / mid).toFixed(1)} lb/hp` } : l;
                              }
                              return l;
                            });
                          })().map((l,i) => {
                            const parts = l.body.split(':');
                            const label = parts[0]?.trim(), raw = parts.slice(1).join(':').trim();
                            const isTimer = /0.60|40.100|60.130/i.test(label);
                            const m = raw.match(/^([-\d.,\u2013]+)\s*(.*)$/);
                            const num = m ? m[1] : raw, unit = m && m[2] ? m[2] : '';
                            return (
                              <div key={i} className={`st-readout ${isTimer ? 'is-hero' : 'is-data'}`}>
                                <div className="st-readout-label">{label}</div>
                                <div className={`st-readout-value${/\u2013/.test(num) ? ' is-range' : ''}`}>{num}{unit && <span className="unit">{unit}</span>}</div>
                                <div className="st-readout-scale"/>
                              </div>
                            );
                          })}
                        </div>
                        {(() => {
                          const trapStat = stats.find(l => /1\/4 mile trap/i.test(l.body));
                          if (!trapStat) return null;
                          const baseline = platform === 'ford' ? 4100 : 4500;
                          const w = parseFloat(raceWeight);
                          const custom = Number.isFinite(w) && w > 0 && w !== baseline;
                          return (
                            <div style={{
                              marginTop:14, padding:'14px 16px', borderRadius:9,
                              background: custom ? 'rgba(0,230,118,0.05)' : 'rgba(0,0,0,0.22)',
                              border:`1px solid ${custom ? 'rgba(0,230,118,0.3)' : T.border}`,
                              transition:'background .2s, border-color .2s',
                            }}>
                              <label style={{
                                display:'block', fontFamily:T.fData, fontSize:11,
                                letterSpacing:'0.14em', textTransform:'uppercase',
                                color: custom ? T.green : T.muted, marginBottom:7, fontWeight:600,
                              }}>
                                Race Weight
                              </label>
                              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                                <input
                                  type="number" min="1000" max="9000" step="25"
                                  placeholder={String(baseline)}
                                  value={raceWeight}
                                  onChange={e => setRaceWeight(e.target.value)}
                                  style={{
                                    width:150, padding:'11px 13px', borderRadius:8,
                                    background:'rgba(0,0,0,0.35)',
                                    border:`1.5px solid ${custom ? 'rgba(0,230,118,0.45)' : T.borderHi}`,
                                    color: custom ? T.green : T.text,
                                    fontFamily:T.fData, fontSize:20, fontWeight:600,
                                    fontVariantNumeric:'tabular-nums slashed-zero',
                                    outline:'none', boxSizing:'border-box',
                                  }}
                                />
                                <span style={{ fontFamily:T.fData, fontSize:14, color:T.muted }}>lb</span>
                              </div>
                              <div style={{ fontSize:13, lineHeight:1.5, fontWeight: custom ? 600 : 400, color: custom ? T.green : T.text }}>
                                {custom
                                  ? `Corrected for ${w} lb — ${w > baseline ? '+' : ''}${(((w - baseline)/100)*0.1).toFixed(1)}s vs ${baseline} lb`
                                  : `Assuming ${baseline} lb with driver`}
                              </div>
                              <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>
                                {custom
                                  ? 'ET, power and lb/hp above are adjusted to this weight.'
                                  : 'Enter your actual race weight to sharpen the estimate.'}
                              </div>
                            </div>
                          );
                        })()}
                        {qtrNote && (
                          <p style={{
                            margin:'12px 0 0', paddingTop:12,
                            borderTop:`1px solid ${T.border}`,
                            fontSize:11.5, lineHeight:1.6, color:T.muted,
                          }}>
                            {qtrNote.body} Horsepower is estimated at the crank from trap speed and race weight.
                          </p>
                        )}
                      </div>
                    )}
                    {oks.length > 0 && (
                      <div style={{ background:T.card, border:'1px solid #2A3339', borderRadius:8, padding:12 }}>
                        <div style={{ fontFamily:T.fDisplay, fontSize:12, fontWeight:700, color:T.green, letterSpacing:2, textTransform:'uppercase', marginBottom:10 }}>All Clear</div>
                        {oks.map((l,i) => <Row key={i} {...l}/>)}
                      </div>
                    )}
                    {infos.length > 0 && (
                      <div style={{ background:T.card, border:'1px solid #2A3339', borderRadius:8, padding:12 }}>
                        {infos.map((l,i) => <Row key={i} {...l}/>)}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── AI Summary cards ── */}
              {!reviewLoading && (reviewAiParts.summary || reviewAiParts.action) && (
                <div style={{ display:'grid', gap:10, marginTop:4 }}>
                  {reviewAiParts.summary && (
                    <div className='st-ai-card st-ai-card-summary'>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                        <span style={{ fontSize:16 }}>🧠</span>
                        <span style={{ fontFamily:T.fDisplay, fontSize:14, fontWeight:700, color:T.green, letterSpacing:1.5, textTransform:'uppercase' }}>Summary</span>
                      </div>
                      <p style={{ fontSize:13, lineHeight:1.75, color:T.text, margin:0 }}>{reviewAiParts.summary}</p>
                    </div>
                  )}
                  {reviewAiParts.action && (
                    <div className='st-ai-card st-ai-card-action'>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                        <span style={{ fontSize:16 }}>🔧</span>
                        <span style={{ fontFamily:T.fDisplay, fontSize:14, fontWeight:700, color:T.ice, letterSpacing:1.5, textTransform:'uppercase' }}>What This Means For You</span>
                      </div>
                      <p style={{ fontSize:13, lineHeight:1.75, color:T.text, margin:0 }}>{reviewAiParts.action}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── COL 2: Leaderboard ────────────────────── */}
          <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
            <div style={css.card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
                <p className='st-section-title' style={{ margin:0 }}>Leaderboard — {interval}</p>
                <button onClick={fetchLeaderboard} disabled={loadingLB} style={{ ...css.btnGhost, fontSize: 12 }}>
                  {loadingLB ? '⏳' : '↻ Refresh'}
                </button>
              </div>

              {/* Filters */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
                <select value={filters.year} onChange={e => setFilters(f => ({ ...f, year: e.target.value }))} style={{ ...css.select, fontSize: 12 }}>
                  <option value="">Year</option>{years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select value={filters.model} onChange={e => setFilters(f => ({ ...f, model: e.target.value }))} style={{ ...css.select, fontSize: 12 }}>
                  <option value="">Model</option>{(platform==='ford' ? fordModels : models).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={filters.engine} onChange={e => setFilters(f => ({ ...f, engine: e.target.value }))} style={{ ...css.select, fontSize: 12 }}>
                  <option value="">Engine</option>{engines.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
                <select value={filters.power} onChange={e => setFilters(f => ({ ...f, power: e.target.value }))} style={{ ...css.select, fontSize: 12 }}>
                  <option value="">Power</option>{powerAdders.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
                <select value={filters.fuel} onChange={e => setFilters(f => ({ ...f, fuel: e.target.value }))} style={{ ...css.select, fontSize: 12 }}>
                  <option value="">Fuel</option>{fuels.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
                <select value={sort} onChange={e => setSort(e.target.value)} style={{ ...css.select, fontSize: 12 }}>
                  <option value="time_seconds:asc">Fastest first</option>
                  <option value="time_seconds:desc">Slowest first</option>
                  <option value="created_at:desc">Newest first</option>
                </select>
              </div>

              {/* Table */}
              {loadingLB ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  {[1,2,3,4,5].map(i => <Skeleton key={i} height={36}/>)}
                </div>
              ) : leaderboard.results.length ? (
                <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${T.border}`, maxHeight: 520, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ background: T.steel }}>
                        {['#','Name','Year','Model','Power','Time',''].map((h, i) => (
                          <th key={i} style={{
                            position: 'sticky', top: 0, background: T.steel,
                            textAlign: i === 5 ? 'right' : i === 6 ? 'center' : 'left',
                            padding: '8px 10px', fontSize: 11, color: T.muted,
                            fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase',
                            borderBottom: `1px solid ${T.border}`,
                            width: [36, 100, 50, 90, 80, 70, 80][i],
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.results.map((r, idx) => {
                        const isTop = idx < 3;
                        return (
                          <tr key={r.id} className={`lb-row${isTop ? ' lb-row-top' : ''}`}>
                            <td style={{ width: 36, textAlign: 'center' }}><RankMedal rank={idx + 1}/></td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.user_alias || 'Anon'}
                            </td>
                            <td style={{ color: T.muted }}>{r.vehicle_year || r.year || '—'}</td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.muted }}>
                              {r.vehicle_model || r.model || '—'}
                            </td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.muted }}>
                              {r.vehicle_power || r.power || '—'}
                            </td>
                            <td className="lb-time" style={{ textAlign: 'right', color: isTop ? T.green : T.text }}>
                              {Number.isFinite(r.time_seconds) ? Number(r.time_seconds).toFixed(2) : '—'}s
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button style={{ ...css.btnGhost, padding: '4px 10px', fontSize: 11 }}
                                onClick={() => overlayLeader(r.id, r.user_alias || 'Leader')}>
                                Overlay
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 0', color: T.faint, fontSize: 13 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🏁</div>
                  No runs yet for this interval
                </div>
              )}

              {leaderboard.total > 0 && (
                <p style={{ fontSize: 11, color: T.faint, marginTop: 8, textAlign: 'right' }}>
                  {leaderboard.total} total run{leaderboard.total !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

        </div>{/* /3-col */}
      </div>

      <FeedbackModal
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
        onSubmit={handleSubmitFeedback}
        defaultPage="/log-comparison"
      />
    </div>
  );
}
